import React, { useEffect, useState, useCallback } from 'react';
import { apiClient } from '../../api/client';
import { EventCard } from './EventCard';
import { TEXT_MUTED, PRIMARY } from '../legacy-ball/table-management/designTokens';

interface Venue {
  id: string;
  name: string;
  address?: string;
  city?: string;
  state?: string;
  parkingInfo?: string | null;
  contactName?: string | null;
  contactPhone?: string | null;
}

interface EventData {
  id: string;
  name: string;
  date: string;
  startTime: string;
  endTime?: string;
  description?: string | null;
  posterUrl?: string | null;
  specialMessage?: string | null;
  programUrl?: string | null;
  mealInfo?: string | null;
  dressCode?: string | null;
  transportInfo?: string | null;
  venue?: Venue | null;
}

/**
 * Sorts events by date ascending, then startTime ascending,
 * then name alphabetically for events with the same date and time.
 */
function sortEvents(events: EventData[]): EventData[] {
  return [...events].sort((a, b) => {
    // Compare dates (ISO string comparison works for date ordering)
    const dateA = a.date.includes('T') ? a.date.split('T')[0] : a.date;
    const dateB = b.date.includes('T') ? b.date.split('T')[0] : b.date;
    if (dateA < dateB) return -1;
    if (dateA > dateB) return 1;

    // Same date — compare start times
    const timeA = a.startTime.includes('T')
      ? a.startTime.split('T')[1].slice(0, 5)
      : a.startTime;
    const timeB = b.startTime.includes('T')
      ? b.startTime.split('T')[1].slice(0, 5)
      : b.startTime;
    if (timeA < timeB) return -1;
    if (timeA > timeB) return 1;

    // Same date and time — sort alphabetically by name
    return a.name.localeCompare(b.name);
  });
}

/**
 * EventsScheduleSection — Renders the Events & Schedule content panel.
 * Fetches events from the API, sorts them, and displays them as EventCards
 * in a vertically scrollable list.
 */
export const EventsScheduleSection: React.FC = () => {
  const [events, setEvents] = useState<EventData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiClient<EventData[]>('/events');
      setEvents(sortEvents(data));
    } catch {
      setError('Failed to load events. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  if (loading) {
    return (
      <div style={styles.centered} role="status" aria-label="Loading events">
        <div style={styles.spinner} />
        <span style={styles.loadingText}>Loading...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.centered}>
        <p style={styles.errorText}>{error}</p>
        <button style={styles.retryButton} onClick={fetchEvents}>
          Retry
        </button>
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div style={styles.centered}>
        <p style={styles.emptyText}>No events scheduled yet.</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {events.map((event) => (
        <EventCard
          key={event.id}
          event={{
            id: event.id,
            name: event.name,
            date: event.date,
            startTime: event.startTime,
            endTime: event.endTime,
            description: event.description,
            posterUrl: event.posterUrl,
            specialMessage: event.specialMessage ?? null,
            programUrl: event.programUrl ?? null,
            mealInfo: event.mealInfo,
            transportInfo: event.transportInfo,
            venueName: event.venue?.name ?? null,
            venueAddress: event.venue?.address ?? null,
            parkingInfo: event.venue?.parkingInfo ?? null,
            parkingImageUrl: event.venue?.parkingImageUrl ?? null,
            dressCode: event.dressCode ?? null,
            contactName: event.venue?.contactName ?? null,
            contactPhone: event.venue?.contactPhone ?? null,
            vendorCount: event._count?.vendorRegistrations ?? 0,
          }}
        />
      ))}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    flex: 1,
    overflowY: 'auto',
    padding: '12px 16px',
  },
  centered: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '32px 16px',
  },
  emptyText: {
    color: TEXT_MUTED,
    fontSize: '0.95rem',
    textAlign: 'center',
    margin: 0,
  },
  loadingText: {
    color: TEXT_MUTED,
    fontSize: '0.9rem',
    marginTop: '8px',
  },
  spinner: {
    width: '24px',
    height: '24px',
    border: `3px solid ${TEXT_MUTED}33`,
    borderTop: `3px solid ${PRIMARY}`,
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  errorText: {
    color: '#d32f2f',
    fontSize: '0.95rem',
    textAlign: 'center',
    margin: '0 0 12px 0',
  },
  retryButton: {
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    padding: '8px 20px',
    fontSize: '0.9rem',
    fontWeight: 500,
    cursor: 'pointer',
  },
};

export default EventsScheduleSection;
